import { Component, OnDestroy, OnInit, Type, ViewChild, ViewContainerRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TestTypeValue, ValuesExchangeProvider } from '@nrwl/common-lib';
import { NavigationProvider } from '@nrwl/navigation-abstract';
import { ServiceTestSetupComponent } from '@nrwl/service-test';
import { ITestSetupEntry } from '@nrwl/test-setup';
import { TruespeedTestSetupComponent } from '@nrwl/truespeed-test';
import { VoipTestSetupComponent } from '@nrwl/voip-test';
import { TwampTestSetupComponent } from '@nrwl/twamp-test';
import { NetworkTestSetupComponent } from '@nrwl/network-test';
import { MultiStepTestSetupComponent } from '@nrwl/multi-step-test';
import { Y1564TestSetupComponent } from '@nrwl/y1564-test';
import { RFC2544TestSetupComponent } from '@nrwl/rfc2544-test';
import { Y1731TestSetupComponent } from '@nrwl/y1731-test';
import { UdpTestSetupComponent } from '@nrwl/udp-test';
import { HttpTestSetupComponent } from '@nrwl/http-test';
import { FtpTestSetupComponent } from '@nrwl/ftp-test';
import { get, cloneDeep } from 'lodash';
import * as moment from 'moment';
import { Subject, Subscription, Unsubscribable } from 'rxjs';
import { loadTestSetup } from './config';
import { loadTestAgentModel } from './config';
import { TestSetupRouterService } from './test-setup-router.service';
import { TwampReflectorTestComponent } from '@nrwl/twamp-reflector-test';
import { BertTestSetupComponent } from '@nrwl/bert-test';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'test-setup-route-starter',
  template: '<ng-template #placeholder></ng-template>'
})
export class TestSetupRouteStarterComponent implements OnInit, OnDestroy {
  @ViewChild('placeholder', { read: ViewContainerRef, static: true })
  viewContainerRef: ViewContainerRef;
  setupId: string;
  //eslint-disable-next-line
  setupConfig: any;
  agentBasedConfig: any;
  dataId: any; //eslint-disable-line @typescript-eslint/no-explicit-any
  component: any; //eslint-disable-line @typescript-eslint/no-explicit-any
  interestSubscr: Unsubscribable;
  routeSubscription: Subscription;
  loadSetup: any; //eslint-disable-line @typescript-eslint/no-explicit-any
  loadAgentModel: any;
  agentModelConfig: any;
  mode = 'new';
  _scheduleId = null;
  _testConfig = null;
  _templateId = null;

  private readonly _unsubscribe: Subject<void> = new Subject();
  constructor(
    private readonly exchange: ValuesExchangeProvider,
    private readonly activatedRoute: ActivatedRoute,
    private readonly navigator: NavigationProvider,
    private readonly testSetupRouterService: TestSetupRouterService
  ) {}

  ngOnInit() {
    this.loadSetup = loadTestSetup();
    this.loadAgentModel = loadTestAgentModel();
    this.exchange.notifyListeners('route_guard', { noValue: true });
    this.interestSubscr = this.exchange.registerInterest(
      'route_guard',
      this.routeGuardListener.bind(this)
    );
    this.routeSubscription = this.activatedRoute.params.subscribe(async params => {
      this.setupId = params.testId;
      this.dataId = this.activatedRoute.snapshot.queryParamMap.get('dataId');
      this.setupConfig = this.loadSetup[this.setupId];
      const defaultParams = JSON.parse(
        get(this.activatedRoute.snapshot.queryParams, 'defaultParams', '{}')
      );
      this.agentModelConfig = cloneDeep(this.loadAgentModel[this.setupId]);
      //test has agent model config, restore default model values based on selected mode
      if (this.agentModelConfig?.agentConfig) {
        const testType = this.resolveTestType();
        this.agentBasedConfig = await this.testSetupRouterService.getConfigBasedOnMode(
          defaultParams,
          this.agentModelConfig,
          testType
        );
        this.mode = this.testSetupRouterService.mode || this.mode;
        this.startSetupTest();
      } else {
        // Test without agent model - used for overlay, etc..
        this.startSetupTest();
      }
    });
  }

  ngOnDestroy() {
    this.interestSubscr.unsubscribe();
    this.routeSubscription.unsubscribe();
    this._unsubscribe.next();
    this._unsubscribe.complete();
    this.testSetupRouterService.onDestroy();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  routeGuardListener(event: any): void {
    if (get(event, 'setupId') === this.setupId) {
      return;
    }
    if (!get(event, 'noValue')) {
      this.component.instance.wizardService.toggleClose(() => {
        this.navigate(event);
        this.exchange.notifyListeners('on_close', event);
      });
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  navigate(event: any): void {
    this.navigator.navigatePerspective(event.id).navigateToTestSetup([event.setupId]);
  }

  startSetupTest(): void {
    const cType = this.resolveSetupEntry();
    this.viewContainerRef.clear();
    this.component = this.viewContainerRef.createComponent(cType);
    (this.component.instance as ITestSetupEntry).setupConfig = this.setupConfig.setupConfig;
    (this.component.instance as ITestSetupEntry).agentBasedConfig = this.agentBasedConfig;
    (this.component.instance as ITestSetupEntry).setupId = this.setupId;
    (this.component.instance as ITestSetupEntry).dataId = { id: this.dataId };
    (this.component.instance as ITestSetupEntry).mode = this.mode;
    (this.component.instance as ITestSetupEntry).templateName =
      this.testSetupRouterService.templateName;
    (this.component.instance as ITestSetupEntry).onClose.subscribe(this.onCloseListener.bind(this));
  }

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  resolveSetupEntry(): Type<any> {
    switch (this.setupId) {
      case 'truespeed_test':
        return TruespeedTestSetupComponent;
      case 'service_test':
        return ServiceTestSetupComponent;
      case 'voip_test':
        return VoipTestSetupComponent;
      case 'network_test':
        return NetworkTestSetupComponent;
      case 'twamp_test':
        return TwampTestSetupComponent;
      case 'multistep_test':
        return MultiStepTestSetupComponent;
      case 'y1564_test':
        return Y1564TestSetupComponent;
      case 'rfc2544_test':
        return RFC2544TestSetupComponent;
      case 'y1731_test':
        return Y1731TestSetupComponent;
      case 'udp_test':
        return UdpTestSetupComponent;
      case 'http_test':
        return HttpTestSetupComponent;
      case 'ftp_test':
        return FtpTestSetupComponent;
      case 'twamp_reflector_test':
        return TwampReflectorTestComponent;
      case 'bert_test':
        return BertTestSetupComponent;
      default: {
        console.error('Unrecognized setupId:', this.setupId);
        return null;
      }
    }
  }

  resolveTestType(): string {
    switch (this.setupId) {
      case 'truespeed_test':
        return TestTypeValue.TRUESPEED;
      case 'service_test':
        return TestTypeValue.SERVICE_CONTINUITY;
      case 'voip_test':
        return TestTypeValue.VOIP;
      case 'network_test':
        return TestTypeValue.NETWORK_QUALITY;
      case 'y1564_test':
        return TestTypeValue.Y1564;
      case 'multistep_test':
        return TestTypeValue.MULTISTEP;
      case 'rfc2544_test':
        return TestTypeValue.RFC2544;
      case 'twamp_test':
        return TestTypeValue.TWAMP;
      case 'twamp_reflector_test':
        return TestTypeValue.TWAMP_REFLECTOR;
      case 'y1731_test':
        return TestTypeValue.Y1731;
      case 'udp_test':
        return TestTypeValue.UDP;
      case 'http_test':
        return TestTypeValue.HTTP;
      case 'ftp_test':
        return TestTypeValue.FTP;
      case 'bert_test':
        return TestTypeValue.BERT;
      default: {
        console.error('Unrecognized setupId:', this.setupId);
        return null;
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  onCloseListener(value: any): void {
    // please note: the navigation below is supposed to 'close' test-setup outlet. the url to be shown
    // will be based on 'activatedRoute.parent' - ie, original page. However, if your original page included
    // query parameters, the navigation from below will discard them.
    // in case you need to preserve query parameters, consider either moving them to path parameter (eg /profile/:userName),
    // or change the relativeTo section into something like this:
    // {relativeTo: this.activatedRoute.parent, queryParamsHandling: 'preserve'}
    // however, this will be not enough to preserve query params - you will need to find places, where 'singlepage' outlet is being
    // opened, and add queryParamsHandling: 'preserve'. For example, ptc-dashboard.component::navigateToSinglePage
    const timestamp = moment().unix();
    this.exchange.notifyListeners('on_close', { value, timestamp });
    this.navigator.navigateToTestSetup(null, { relativeTo: this.activatedRoute.parent });
  }
}
